import { useCallback } from 'react'
import { usDateToFormDate } from 'admin/lib/dates'

const timeRegex = /^\d\d:\d\d$/
const twoDigitDateRegex = /\d{1,2}(-|\/|) *\d{1,2}\1 *\d{2}/
const fourDigitDateRegex = /\d{1,2}(-|\/|) *\d{1,2}\1 *\d{4}/
const fullDateTimeRegex = /\d{1,2}(-|\/|) *\d{1,2}\1 *\d{4} \d\d:\d\d/
const usDateTimeRegex =
  /^\d{1,2}(-|\/|) *\d{1,2}\1 *\d{4}, \d{1,2}:\d{2} (AM|PM)$/

// NOTE: We still have an issue where Warning validation is not triggered after clicking. There is a
// skipped test proving this. To solve it we may need to review how the Warning validation is triggered.
const standardSetValueOptions = { shouldValidate: true, shouldDirty: true }

export function useExtractionValueClicked(setValue) {
  return useCallback(
    value => {
      const element = document.activeElement

      // handle US date fields, these will have an ID that starts with 'usDate'
      if (element.id.startsWith('usDate')) {
        // Replace any hyphens with slash and add a comma after before the first space
        let formattedValue = value.replace(/-/g, '/').replace(/ /, ', ')
        // Check to see if a time is not passed in with date
        if (
          fourDigitDateRegex.test(formattedValue) &&
          formattedValue.indexOf(':') < 0
        ) {
          formattedValue += ', 00:00 AM'
        }

        // Check that we endWith 'AM' or 'PM', if not add it and change the hour if required
        if (!formattedValue.endsWith('AM') && !formattedValue.endsWith('PM')) {
          let hour = parseInt(
            formattedValue.substring(
              formattedValue.indexOf(':') - 2,
              formattedValue.indexOf(':')
            )
          )

          let amPm = 'AM'
          if (hour > 11) {
            amPm = 'PM'
          }
          if (hour > 12) {
            hour = hour - 12
            formattedValue = `${formattedValue.substring(
              0,
              formattedValue.indexOf(':') - 2
            )}${hour}${formattedValue.substring(
              formattedValue.indexOf(':')
            )} ${amPm}`
          } else {
            formattedValue = `${formattedValue} ${amPm}`
          }
        }

        // Check to see if we now match US DateTime format
        if (usDateTimeRegex.test(formattedValue)) {
          // Convert from US date to form date (ISO) format
          const formDate = usDateToFormDate(formattedValue)
          // Update related field value
          const input = document.getElementsByName(
            element.getAttribute('relatedfieldpath')
          )[0]

          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value'
          ).set
          nativeInputValueSetter.call(input, formDate)
          const ev1 = new Event('input', { bubbles: true })

          input.dispatchEvent(ev1)
        }

        return
      }

      switch (element.type) {
        case 'text':
        case 'textarea':
        case 'search':
          setValue(
            element.name,
            `${element.value} ${value}`.trim(),
            standardSetValueOptions
          )
          break
        case 'datetime-local':
          // First check if new value contains time
          if (timeRegex.test(value)) {
            const currentDate = element.value
            const splitCurrentDate = currentDate.split('T')

            if (splitCurrentDate[0]) {
              setValue(
                element.name,
                `${currentDate.split('T')[0]}T${value}`,
                standardSetValueOptions
              )
            }
          }
          if (fullDateTimeRegex.test(value)) {
            setDate(setValue, fullDateTimeRegex, value, element, '')
          } else if (fourDigitDateRegex.test(value)) {
            setDate(setValue, fourDigitDateRegex, value, element, '')
          } else if (twoDigitDateRegex.test(value)) {
            setDate(setValue, twoDigitDateRegex, value, element, '20')
          }
          break
        case 'number':
          setValue(element.name, value.trim(), standardSetValueOptions)
          break
      }
    },
    [setValue]
  )
}

function setDate(setValue, regex, value, element, yearComplement) {
  const date = regex.exec(value)

  if (date != null) {
    let time =
      element.value.split('T').length > 1
        ? element.value.split('T')[1]
        : '00:00'
    const splitRegex = /[-*/* *]/
    const splitDates = date[0].split(splitRegex)

    // If the splitted date-time is in 4 parts
    // then time is likely to be included so use this value
    if (splitDates.length > 3) {
      time = splitDates[3]
    }

    if (splitDates.length > 2) {
      if (splitDates[0].length === 1) {
        splitDates[0] = `0${splitDates[0]}`
      }
      if (splitDates[1].length === 1) {
        splitDates[1] = `0${splitDates[1]}`
      }

      setValue(
        element.name,
        `${yearComplement}${splitDates[2]}-${splitDates[1]}-${splitDates[0]}T${time}`,
        standardSetValueOptions
      )
    }
  }
}
